import React, { useState, useEffect } from "react";
import styles from "./UsageLogs.module.css";
import { Bar } from "react-chartjs-2";
import { useGetPostsQuery } from "../../../api/apiConfig";

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
  }[];
}

const UsageLogs: React.FC = () => {
  const [view, setView] = useState("Printed Items");
  const [dateRange, setDateRange] = useState("Last 1 Month");
  const [printedItemsThisMonth, setPrintedItemsThisMonth] = useState(0);
  const [chartData, setChartData] = useState<ChartData>({
    labels: [],
    datasets: []
  });
  const [queryParams, setQueryParams] = useState({
    fromDate: "",
    toDate: "",
    status: "S"
  });

  const getStatus = () => (view === "Printed Items" ? "S" : "U");

  const calculateDateRange = (range: string) => {
    let now = new Date();
    now.setDate(now.getDate() - 1);
    let fromDate = new Date();
    fromDate.setUTCHours(0, 0, 0, 0);
    switch (range) {
      case "Last 1 Month":
        fromDate.setMonth(now.getMonth() - 1);
        break;
      case "Last 6 Months":
        fromDate.setMonth(now.getMonth() - 6);
        break;
      case "Last One Year":
        fromDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        fromDate.setMonth(now.getMonth() - 1);
    }
    return { fromDate: fromDate.toISOString(), toDate: now.toISOString() };
  };

  useEffect(() => {
    const { fromDate, toDate } = calculateDateRange(dateRange);
    const status = getStatus();

    setTimeout(() => {
      setQueryParams({ fromDate, toDate, status });
    }, 1000);
  }, [view, dateRange]);

  const {
    isLoading: isUsageLogLoading,
    isError: isUsageLogError,
    isSuccess: isUsageLogSuccess,
    data: usageLogData,
    refetch: fetchUsageLogs
  } = useGetPostsQuery(
    `PrintingRequest/GetUsageDetail?fromDate=${queryParams.fromDate}&toDate=${queryParams.toDate}&Status=${queryParams.status}`
  );

  useEffect(() => {
    if (isUsageLogSuccess && usageLogData) {
      const labels: string[] = [];
      const data: number[] = [];

      if (dateRange === "Last 1 Month") {
        const startDate = new Date(queryParams.fromDate);
        const endDate = new Date(queryParams.toDate);
        const dateMap: { [key: string]: number } = {};

        usageLogData?.Model?.forEach((record: any) => {
          const date = new Date(
            record.Year,
            new Date(`${record.Month} 1, ${record.Year}`).getMonth(),
            record.Day
          );
          const dateString = date.toLocaleDateString("default", {
            day: "numeric",
            month: "short",
            year: "2-digit"
          });
          dateMap[dateString] = (dateMap[dateString] || 0) + record.Count;
        });

        for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateString = d.toLocaleDateString("default", {
            day: "numeric",
            month: "short",
            year: "2-digit"
          });
          labels.push(dateString);
          data.push(dateMap[dateString] || 0);
        }
      } else {
        const monthMap: { [key: string]: number } = {};

        usageLogData?.Model?.forEach((record: any) => {
          const monthLabel = new Date(
            record.Year,
            new Date(`${record.Month} 1, ${record.Year}`).getMonth()
          ).toLocaleString("default", {
            month: "short",
            year: "2-digit"
          });
          monthMap[monthLabel] = (monthMap[monthLabel] || 0) + record.Count;
        });

        const startDate = new Date(queryParams.fromDate);
        const endDate = new Date(queryParams.toDate);

        for (let d = startDate; d <= endDate; d.setMonth(d.getMonth() + 1)) {
          const monthLabel = d.toLocaleString("default", {
            month: "short",
            year: "2-digit"
          });
          labels.push(monthLabel);
          data.push(monthMap[monthLabel] || 0);
        }
      }

      setChartData({
        labels,
        datasets: [
          {
            label:
              view === "Printed Items"
                ? "Number of Items Printed"
                : "Number of Items Failed",
            data,
            backgroundColor: "#f06c00"
          }
        ]
      });

      const currentDate = new Date();
      const currentMonthData = usageLogData?.Model?.filter(
        (record: any) =>
          record.Year === currentDate.getFullYear() &&
          new Date(`${record.Month} 1, ${record.Year}`).getMonth() ===
            currentDate.getMonth()
      );

      const totalCurrentMonthCount = currentMonthData?.reduce(
        (sum: number, record: any) => sum + record.Count,
        0
      );

      setPrintedItemsThisMonth(totalCurrentMonthCount);
    }
  }, [isUsageLogSuccess, usageLogData, view, dateRange, queryParams]);
  useEffect(() => {
    fetchUsageLogs();
  }, [queryParams, fetchUsageLogs]);

  return (
    <div className={styles.container}>
      <h4>Usage</h4>
      <p>Track usage over time for printed items in Smartprint.</p>
      <div className={styles.circle}>
        <div className={styles.circleTitle}>
          {view === "Printed Items" ? "PRINTED ITEMS" : "FAILED ITEMS"}
        </div>
        <div className={styles.circleNumber}>{printedItemsThisMonth}</div>
        <div className={styles.circleFooter}>This month</div>
      </div>
      <h4>Usage Charts</h4>
      <div className={styles.dropdownContainer}>
        <select
          className={styles.dropdown}
          value={view}
          onChange={(e) => setView(e.target.value)}
        >
          <option>Printed Items</option>
          <option>Failed Items</option>
        </select>
        <select
          className={styles.dropdown}
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
        >
          <option>Last 1 Month</option>
          <option>Last 6 Months</option>
          <option>Last One Year</option>
        </select>
      </div>
      {isUsageLogLoading ? (
        <div>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <div className={styles.loadingText}>Loading...</div>
          </div>
        </div>
      ) : isUsageLogError ? (
        <p>Error loading data.</p>
      ) : (
        <Bar data={chartData} />
      )}
    </div>
  );
};

export default UsageLogs;
